import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { unarchiveMailsOnImap } from '../server/unarchiveMailOnImap.js';
import { getCurrentUserID } from './currentUserID.js';

/**
 * Restores one or more archived mails back into the active list. To keep the IMAP
 * folder the single source of truth, the message is moved out of the archive folder
 * back into the INBOX (located by its Message-ID, since UIDs are per-folder) and the
 * document's archive flag is cleared. `emailID` is realigned to the new INBOX UID so
 * the INBOX mail sync recognises the message and does not re-import it as a duplicate.
 *
 * Mails without a Message-ID (legacy) cannot be located on the server and are only
 * flagged locally. Returns the number of mails newly restored.
 */
export const unarchiveMails = registerMutation({
  type: () => Int,
  operationName: 'unarchiveMails',
  resolve: (mailIDs: string[]) => {
    return async () => {
      const userID = getCurrentUserID();
      if (!userID) throw new Error('Nicht angemeldet');
      if (!mailIDs.length) return 0;

      const mailCollection = await getRawCollection<EntityData<MailEntity>>(MailEntity._collectionName);
      const mails = await mailCollection.find({ _id: { $in: mailIDs }, archivedAt: { $ne: null } }).toArray();
      if (!mails.length) return 0;

      const messageIDs = mails.map(mail => mail.messageID).filter((id): id is string => !!id);
      const movedUidByMessageID = await unarchiveMailsOnImap(messageIDs);

      const operations = mails.map(mail => {
        const newUid = mail.messageID ? movedUidByMessageID.get(mail.messageID) : undefined;
        return {
          updateOne: {
            filter: { _id: mail._id },
            update: {
              $set: { archivedAt: null, mailboxFolder: 'INBOX', ...(newUid ? { emailID: newUid } : {}) },
            },
          },
        };
      });
      const result = await mailCollection.bulkWrite(operations);
      return result.modifiedCount;
    };
  },
  params: [{ name: 'mailIDs', type: () => [String] }],
});
